import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function timeAgo(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const now = Date.now();
  const s = Math.max(0, Math.round((now - then) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}

export function useAskRitesh({ pollInterval = 20000, limit } = {}) {
  const [questions, setQuestions] = useState([]);
  const [stats, setStats] = useState({
    total_approved: 0,
    total_answered: 0,
    total_pending: 0,
  });

  const refresh = async () => {
    try {
      const [q, s] = await Promise.all([
        axios.get(`${API}/questions${limit ? `?limit=${limit}` : ""}`),
        axios.get(`${API}/questions/stats`),
      ]);
      setQuestions(q.data);
      setStats(s.data);
    } catch (_) {
      /* soft-fail */
    }
  };

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, pollInterval);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pollInterval, limit]);

  return { questions, stats, refresh };
}

/** Form used both on landing section and on /questions page. */
export function AskRiteshForm({ onSubmitted, compact = false }) {
  const [form, setForm] = useState({ name: "", city: "", question: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || form.question.length < 8) {
      toast.error("Add your name and a question of at least 8 characters.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/questions`, form);
      setDone(true);
      setForm({ name: "", city: "", question: "" });
      toast.success("Question sent — Ritesh will review it before publishing.");
      onSubmitted?.();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        data-testid="ask-success"
        className="rounded-2xl border border-[#D4AF37]/30 bg-gradient-to-b from-[#141005] to-[#0a0a0a] p-8 md:p-10 text-center"
      >
        <div className="mx-auto w-12 h-12 rounded-full border border-[#D4AF37]/50 flex items-center justify-center mb-5">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path
              d="M5 12l5 5L20 7"
              stroke="#D4AF37"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <div className="font-display text-white text-2xl md:text-3xl leading-tight">
          Question sent.
        </div>
        <p className="mt-3 text-white/65 text-sm md:text-base max-w-md mx-auto">
          Ritesh reviews every question personally before it appears on the
          public wall. You'll see it here once he approves & replies.
        </p>
        <button
          onClick={() => setDone(false)}
          className="mt-6 text-[#D4AF37] hover:text-[#f3d568] text-sm inline-flex items-center gap-2"
          data-testid="ask-another"
        >
          Ask another question
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
            <path d="M5 12h14M13 5l7 7-7 7" stroke="currentColor" strokeWidth="2" />
          </svg>
        </button>
      </motion.div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid="ask-form"
      className={`rounded-2xl border border-white/8 bg-white/[0.02] backdrop-blur-xl ${
        compact ? "p-6 md:p-8" : "p-6 md:p-10"
      }`}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 md:gap-x-8 gap-y-5">
        <div>
          <label className="text-[10px] uppercase tracking-[0.28em] text-white/45">
            Your name
          </label>
          <input
            data-testid="ask-name"
            value={form.name}
            onChange={set("name")}
            required
            placeholder="Jane Doe"
            className="line-input mt-1"
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.28em] text-white/45">
            City (optional)
          </label>
          <input
            data-testid="ask-city"
            value={form.city}
            onChange={set("city")}
            placeholder="Mumbai"
            className="line-input mt-1"
          />
        </div>
        <div className="md:col-span-2">
          <label className="text-[10px] uppercase tracking-[0.28em] text-white/45">
            Your question
          </label>
          <textarea
            data-testid="ask-question"
            value={form.question}
            onChange={set("question")}
            required
            rows={compact ? 3 : 4}
            placeholder="Ask anything about your venue — profit, staff, menu, pre-launch, guest journey…"
            className="line-input mt-1 resize-none"
            maxLength={800}
          />
          <div className="mt-1 text-right text-[11px] text-white/35 tabular-nums">
            {form.question.length}/800
          </div>
        </div>
      </div>

      <div className="mt-6 md:mt-8 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4">
        <p className="text-white/45 text-xs md:text-[13px] max-w-md leading-relaxed">
          Questions are moderated. Ritesh personally reviews and replies before
          your question appears on the public wall.
        </p>
        <button
          type="submit"
          disabled={loading}
          data-testid="ask-submit"
          className="group inline-flex items-center justify-center gap-3 rounded-full bg-[#D4AF37] text-black px-6 sm:px-7 py-3.5 sm:py-4 text-sm font-medium hover:bg-[#f3d568] transition-colors disabled:opacity-60 w-full sm:w-auto"
        >
          {loading ? "Sending…" : "Send Question"}
          <span className="w-5 h-5 rounded-full bg-black/10 flex items-center justify-center group-hover:translate-x-1 transition-transform">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
              <path
                d="M5 12h14M13 5l7 7-7 7"
                stroke="currentColor"
                strokeWidth="2"
              />
            </svg>
          </span>
        </button>
      </div>
    </form>
  );
}

/** Single question card on the public wall. */
export function QuestionCard({ q, idx = 0, compact = false }) {
  return (
    <motion.article
      initial={{ opacity: 0, y: 22 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.6, delay: (idx % 6) * 0.06 }}
      data-testid={`question-card-${q.id}`}
      className={`rounded-2xl border border-white/8 bg-white/[0.02] backdrop-blur-xl overflow-hidden flex flex-col ${
        compact ? "p-5 md:p-6" : "p-6 md:p-8"
      }`}
    >
      <header className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-[#0f0f0f] border border-white/10 flex items-center justify-center text-[#D4AF37] font-display text-sm">
            {q.name?.slice(0, 1)?.toUpperCase() || "?"}
          </div>
          <div>
            <div className="text-white text-sm">{q.name}</div>
            <div className="text-white/45 text-[11px] uppercase tracking-[0.22em]">
              {q.city ? `${q.city} · ` : ""}
              {timeAgo(q.created_at)}
            </div>
          </div>
        </div>
        {q.answered ? (
          <span className="text-[10px] uppercase tracking-[0.22em] text-black bg-[#D4AF37] px-2.5 py-1 rounded-full whitespace-nowrap">
            Answered
          </span>
        ) : (
          <span className="text-[10px] uppercase tracking-[0.22em] text-white/60 border border-white/15 px-2.5 py-1 rounded-full whitespace-nowrap">
            On the wall
          </span>
        )}
      </header>

      <p className="mt-5 text-white text-[15px] md:text-base leading-relaxed">
        {q.question}
      </p>

      {q.answered && (
        <div className="mt-5 pt-5 border-t border-white/8">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] text-[#D4AF37] mb-3">
            <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37]" />
            Ritesh replied
          </div>
          <p className="text-white/80 text-[15px] md:text-base leading-relaxed whitespace-pre-wrap font-editorial italic">
            {q.answer}
          </p>
        </div>
      )}
    </motion.article>
  );
}

/** Landing section wrapper (`#ask` anchor). Shows counter + first 6 Qs + form + See-all link. */
export default function AskRitesh() {
  const { questions, stats, refresh } = useAskRitesh({ limit: 10 });

  return (
    <section
      id="ask"
      data-testid="ask-section"
      className="relative py-20 md:py-40 bg-[#030303] overflow-hidden"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 w-[700px] h-[700px] rounded-full"
        style={{
          background:
            "radial-gradient(closest-side, rgba(212,175,55,0.10), rgba(212,175,55,0) 65%)",
        }}
      />

      <div className="relative max-w-[1440px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-end mb-12 md:mb-16">
          <div className="lg:col-span-8">
            <div className="text-[11px] uppercase tracking-[0.28em] text-[#D4AF37] mb-5">
              Ch. 09.5 &mdash; Ask Ritesh
            </div>
            <h2 className="font-display text-white text-[30px] sm:text-4xl md:text-6xl lg:text-[80px] leading-[1.02] sm:leading-[0.98] tracking-[-0.02em] uppercase">
              Ask Ritesh a question about{" "}
              <em className="font-editorial italic text-[#D4AF37] font-light normal-case">
                your business.
              </em>
            </h2>
          </div>
          <div className="lg:col-span-4 text-white/60 text-sm md:text-base leading-relaxed lg:pl-6">
            A public wall. Anyone can ask. Only Ritesh replies. Every reply is
            moderated before it appears — no noise, no spam.
          </div>
        </div>

        {/* Live counter */}
        <LiveCounter stats={stats} />

        {/* Form */}
        <div className="mt-10 md:mt-14 max-w-3xl">
          <AskRiteshForm onSubmitted={refresh} compact />
        </div>

        {/* Recent questions grid */}
        <div className="mt-12 md:mt-16">
          <div className="flex items-end justify-between mb-6 md:mb-8">
            <div className="text-[11px] uppercase tracking-[0.28em] text-white/60">
              On the wall
            </div>
            <a
              href="/questions"
              target="_blank"
              rel="noopener"
              data-testid="ask-see-all"
              className="text-[#D4AF37] hover:text-[#f3d568] text-sm inline-flex items-center gap-2 gold-underline"
            >
              See every question
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                <path d="M7 17L17 7M17 7H8M17 7v9" stroke="currentColor" strokeWidth="2" />
              </svg>
            </a>
          </div>

          {questions.length === 0 ? (
            <EmptyWall />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              <AnimatePresence>
                {questions.map((q, i) => (
                  <QuestionCard key={q.id} q={q} idx={i} compact />
                ))}
              </AnimatePresence>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function EmptyWall() {
  return (
    <div className="rounded-2xl border border-dashed border-white/12 bg-white/[0.015] p-10 md:p-14 text-center">
      <div className="mx-auto w-10 h-10 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center mb-4">
        <span className="w-2 h-2 rounded-full bg-[#D4AF37] animate-pulse" />
      </div>
      <div className="font-display text-white text-2xl">Be the first to ask.</div>
      <p className="mt-3 text-white/55 text-sm max-w-md mx-auto">
        No questions have been approved to the public wall yet. Yours could be
        the one that starts the conversation.
      </p>
    </div>
  );
}

export function LiveCounter({ stats }) {
  return (
    <div className="grid grid-cols-3 gap-px bg-white/8 rounded-2xl overflow-hidden border border-white/8 max-w-3xl">
      {[
        { k: stats.total_answered, label: "Answered on the wall", accent: true },
        { k: stats.total_approved, label: "Questions live" },
        { k: stats.total_pending, label: "In moderation" },
      ].map((s, i) => (
        <div
          key={s.label}
          data-testid={`counter-${i}`}
          className="bg-[#050505] px-5 md:px-8 py-6 md:py-8 flex flex-col justify-between min-h-[120px]"
        >
          <div className="text-[10px] uppercase tracking-[0.24em] text-white/45">
            {String(i + 1).padStart(2, "0")}
          </div>
          <div>
            <div
              className={`font-display text-[36px] md:text-5xl leading-none tabular-nums ${
                s.accent ? "text-[#D4AF37]" : "text-white"
              }`}
            >
              {s.k}
            </div>
            <div className="mt-2 text-white/55 text-[11px] md:text-sm">
              {s.label}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
